/**
 * Lo que comparten «Verde, últimas 24 horas» (docs/esquema.md §5.5, §12.11) y «Producción, últimas
 * 24 horas» (§12.20): la ventana que fija el servidor y las reglas de la fusión por identidad que
 * no saben de viajes ni de silos. Cada estación arma sus filas; acá viven los bordes, la regla del
 * recibo contra la marca del snapshot, el orden y la lista de personas.
 */

import { HORA_MS, MINUTO_MS, instanteLocal } from './tiempo';

/** La ventana: lo cargado en las últimas 24 horas (Nicolás, 5/10: a las 07:00 «hoy» estaba vacío). */
export const VENTANA_RECIENTE_MS = 24 * HORA_MS;
/** Hacia adelante: una hora declarada apenas en el futuro por un reloj corrido entra igual; más allá, no. */
export const TOLERANCIA_RECIENTE_MS = 60 * MINUTO_MS;

/** Los dos bordes para un instante: se calculan UNA vez y se bindean a todas las consultas. */
export function ventanaReciente(ahoraMs: number): { desde: string; hasta: string } {
	return { desde: instanteLocal(ahoraMs - VENTANA_RECIENTE_MS), hasta: instanteLocal(ahoraMs + TOLERANCIA_RECIENTE_MS) };
}

/** Lo que toda fila de una lista reciente lleva, sea de la estación que sea. */
export interface FilaBase {
	clave: string;
	/** Hora del hecho, o null en un recibo viejo sin ella. */
	hora: string | null;
	/** Por qué se ordena: la hora del hecho o, sin ella, la del acuse en hora del negocio. */
	orden: string;
	personas: string[];
	este_telefono: boolean;
	origen: 'catalogo' | 'recibo' | 'pendiente';
	corregido: boolean;
	/** De una pendiente: su id de outbox y su epoch, para la nota de «esperando señal». */
	id?: string;
	epoch?: string;
}

/** La hora del acuse (ISO UTC) como instante del negocio, para ordenar un recibo sin hora del hecho. */
export function ordenDelAcuse(sincronizadoEn: string | undefined): string {
	const ms = sincronizadoEn === undefined ? Number.NaN : Date.parse(sincronizadoEn);
	return Number.isNaN(ms) ? '' : instanteLocal(ms);
}

/** Regla 0: dónde cae un instante respecto de la ventana cerrada `[desde, hasta]` del snapshot. */
export function ubicarEnVentana(orden: string, desde: string, hasta: string): 'dentro' | 'antes' | 'despues' {
	if (orden > hasta) return 'despues';
	if (orden < desde) return 'antes';
	return 'dentro';
}

/**
 * Reglas 2 y 3: una pendiente entra siempre; un recibo entra solo si su raíz no está en el catálogo
 * Y es más nuevo que el snapshot (con la marca ausente, entra). Si la raíz está, el catálogo trae la
 * versión vigente, que puede ser una corrección de gestión; si no está y es más viejo, gestión lo anuló.
 */
export function entraElPropio(
	p: { pendiente: boolean; raiz_id: string | null; sincronizado_en?: string },
	raices: ReadonlySet<string>,
	horaDelCatalogo: string | undefined,
): boolean {
	if (p.pendiente) return true;
	if (p.raiz_id !== null && raices.has(p.raiz_id)) return false;
	if (horaDelCatalogo !== undefined && p.sincronizado_en !== undefined && p.sincronizado_en <= horaDelCatalogo) return false;
	return true;
}

/** Regla 4: los que tienen hora, por hora descendente; los sin hora, después, por su acuse. Ordena en el lugar. */
export function ordenarRecientes<F extends { hora: string | null; orden: string }>(filas: F[]): F[] {
	return filas.sort((a, b) => {
		if ((a.hora === null) !== (b.hora === null)) return a.hora === null ? 1 : -1;
		return b.orden.localeCompare(a.orden);
	});
}

/** Las personas que aparecen, en orden de aparición: los chips de la pantalla. */
export function personasDe(filas: Array<{ personas: string[] }>): string[] {
	const personas: string[] = [];
	for (const f of filas) for (const id of f.personas) if (!personas.includes(id)) personas.push(id);
	return personas;
}

/** Lo que una pendiente deja en su fila para la nota de «esperando señal»; nada en un recibo o en el catálogo. */
export function marcaDePendiente(p: { id?: string; epoch?: string }): { id?: string; epoch?: string } {
	return { ...(p.id === undefined ? {} : { id: p.id }), ...(p.epoch === undefined ? {} : { epoch: p.epoch }) };
}
